import axios from 'axios';
import {
  isValidFogoAddress,
  isValidSolanaAddress,
  isValidStellarContractAddress,
  isValidSuiAddress,
} from 'src/common/utils/non-evm-currency';
import { currencyService } from 'src/modules/currencies/infra/currencyService';

export interface NonEvmTokenMetadata {
  symbol: string;
  name: string;
  decimals: number;
  logoURI?: string;
  isVerified: boolean;
}

export interface NonEvmTokenResolutionResult {
  metadata: NonEvmTokenMetadata | null;
  source: 'internal' | 'external' | 'fallback';
  error?: string;
}

export type NonEvmChain = 'solana' | 'sui' | 'fogo' | 'stellar';

/** Stellar amounts are int64 with 7 decimals, network-wide — not the 9 the other chains default to. */
const STELLAR_DECIMALS = 7;
const DEFAULT_DECIMALS = 9;

function fallbackDecimals(chain: NonEvmChain): number {
  return chain === 'stellar' ? STELLAR_DECIMALS : DEFAULT_DECIMALS;
}

interface CoinGeckoSolanaTokenResponse {
  id: string;
  symbol: string;
  name: string;
  detail_platforms?: {
    solana?: {
      decimal_place?: number;
      contract_address?: string;
    };
  };
  image?: {
    large?: string;
    small?: string;
    thumb?: string;
  };
}

// Cache for token metadata to avoid repeated API calls
const tokenCache = new Map<string, { data: NonEvmTokenMetadata; timestamp: number }>();
const CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds

/**
 * Generates cache key for token
 */
function getCacheKey(address: string, chain: NonEvmChain): string {
  // Stellar (base32) and Solana/Fogo (base58) addresses are case-sensitive, so lowercasing would
  // collapse distinct addresses onto one key. Only hex addresses are safe to normalize.
  const normalized = address.startsWith('0x') ? address.toLowerCase() : address;
  return `${chain}:${normalized}`;
}

/**
 * Checks if cached data is still valid
 */
function isCacheValid(timestamp: number): boolean {
  return Date.now() - timestamp < CACHE_DURATION;
}

const CHAIN_IDENTIFIER_MAP: Record<NonEvmChain, string> = {
  solana: '101',
  fogo: '102',
  sui: '103',
  stellar: '148',
};

/**
 * Fetches token metadata from the currency API
 */
async function fetchFromCurrencyApi(address: string, chain: NonEvmChain): Promise<NonEvmTokenMetadata | null> {
  try {
    const chainIdentifier = CHAIN_IDENTIFIER_MAP[chain];
    const currency = await currencyService.getByAddressAndChain(address, chainIdentifier);

    return {
      symbol: currency.name,
      name: currency.name,
      decimals: currency.decimals,
      logoURI: currency.logo_uri ?? undefined,
      isVerified: true,
    };
  } catch {
    return null;
  }
}

/**
 * Fetches Solana token metadata from CoinGecko API
 */
async function fetchSolanaTokenFromCoinGecko(address: string): Promise<NonEvmTokenMetadata | null> {
  try {
    const response = await axios.get<CoinGeckoSolanaTokenResponse>(
      `https://api.coingecko.com/api/v3/coins/solana/contract/${address}`,
      {
        timeout: 5000,
        headers: {
          Accept: 'application/json',
        },
      },
    );

    const data = response.data;

    if (!data || !data.symbol) {
      return null;
    }

    return {
      symbol: data.symbol.toUpperCase(),
      name: data.name,
      decimals: data.detail_platforms?.solana?.decimal_place ?? 9,
      logoURI: data.image?.small || data.image?.large,
      isVerified: true,
    };
  } catch (error) {
    console.warn('CoinGecko Solana API error:', error);
    return null;
  }
}

/**
 * Fetches Sui token metadata from CoinGecko API
 * Note: CoinGecko doesn't have a dedicated Sui contract endpoint yet,
 * so this is a placeholder for future implementation
 */
async function fetchSuiTokenFromCoinGecko(_address: string): Promise<NonEvmTokenMetadata | null> {
  // TODO: Implement when CoinGecko adds Sui support
  // For now, return null to trigger fallback
  return null;
}

/**
 * Resolves non-EVM token metadata with fallback strategy
 * 1. Check local currency database
 * 2. Try external API (CoinGecko)
 * 3. Return fallback (masked address)
 */
export async function resolveNonEvmTokenMetadata(
  address: string,
  chain: NonEvmChain,
): Promise<NonEvmTokenResolutionResult> {
  // Validate address format based on chain
  if (!address) {
    return {
      metadata: null,
      source: 'fallback',
      error: 'Address is required',
    };
  }

  if (chain === 'solana' && !isValidSolanaAddress(address)) {
    return {
      metadata: null,
      source: 'fallback',
      error: 'Invalid Solana address format',
    };
  }

  if (chain === 'sui' && !isValidSuiAddress(address)) {
    return {
      metadata: null,
      source: 'fallback',
      error: 'Invalid Sui address format',
    };
  }

  if (chain === 'fogo' && !isValidFogoAddress(address)) {
    return {
      metadata: null,
      source: 'fallback',
      error: 'Invalid Fogo address format',
    };
  }

  if (chain === 'stellar' && !isValidStellarContractAddress(address)) {
    return {
      metadata: null,
      source: 'fallback',
      error: 'Invalid Stellar contract address',
    };
  }

  const cacheKey = getCacheKey(address, chain);

  // Check cache first
  const cached = tokenCache.get(cacheKey);
  if (cached && isCacheValid(cached.timestamp)) {
    return {
      metadata: cached.data,
      source: cached.data.isVerified ? 'internal' : 'external',
    };
  }

  try {
    // Step 1: Try currency API first
    let metadata = await fetchFromCurrencyApi(address, chain);

    if (metadata) {
      // Cache the result
      tokenCache.set(cacheKey, {
        data: metadata,
        timestamp: Date.now(),
      });

      return {
        metadata,
        source: 'internal',
      };
    }

    // Step 2: Try external APIs
    if (chain === 'solana') {
      metadata = await fetchSolanaTokenFromCoinGecko(address);
    } else if (chain === 'sui') {
      metadata = await fetchSuiTokenFromCoinGecko(address);
    } else if (chain === 'fogo' || chain === 'stellar') {
      // Neither has a CoinGecko contract endpoint; registered tokens resolve through the currency
      // API above, anything else falls back to the masked address with a manually entered name.
      metadata = null;
    }

    if (metadata) {
      // Cache the result
      tokenCache.set(cacheKey, {
        data: metadata,
        timestamp: Date.now(),
      });

      return {
        metadata,
        source: 'external',
      };
    }

    // Step 3: Return fallback
    const maskedAddress = `${address.slice(0, 6)}...${address.slice(-4)}`;
    return {
      metadata: {
        symbol: maskedAddress,
        name: maskedAddress,
        decimals: fallbackDecimals(chain),
        isVerified: false,
      },
      source: 'fallback',
      error: 'Token not found in any data source',
    };
  } catch (error) {
    console.error('Non-EVM token metadata resolution error:', error);

    // Return fallback on error
    const maskedAddress = `${address.slice(0, 6)}...${address.slice(-4)}`;
    return {
      metadata: {
        symbol: maskedAddress,
        name: maskedAddress,
        decimals: fallbackDecimals(chain),
        isVerified: false,
      },
      source: 'fallback',
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Clears the token metadata cache
 */
export function clearNonEvmTokenCache(): void {
  tokenCache.clear();
}

/**
 * Gets cache statistics for debugging
 */
export function getNonEvmCacheStats(): { size: number; keys: string[] } {
  return {
    size: tokenCache.size,
    keys: Array.from(tokenCache.keys()),
  };
}
